/**
 * User Management API Axios Instance
 * Dedicated axios instance for User Management MicroService API calls
 */

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { v4 as uuidv4 } from "uuid";
import { addAuthHeader, handle401Error } from "../utils/auth/axiosInterceptor";

const userManagementHttps = axios.create({
  baseURL: `${import.meta.env.VITE_REACT_USER_MANAGEMENT_API || "http://localhost:9008"}/v1/userManagement`,
  timeout: 50000,
});

// Add request interceptor to generate unique headers and add Authorization
userManagementHttps.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Generate unique X-Message-Id and X-Correlation-Id for each request
    if (config.headers) {
      config.headers["X-Message-Id"] = uuidv4();
      config.headers["X-Correlation-Id"] = uuidv4();
    }
    // Skip Authorization header for validateToken and resetPassword endpoints (they don't require auth)
    const url = config.url || "";
    const isValidateToken = url.includes("/validateToken") || url.includes("validateToken");
    const isResetPassword = url.includes("/resetPassword") || url.includes("resetPassword");
    const shouldSkipAuth = isValidateToken || isResetPassword;
    console.log("🔵 userManagement axios: Request interceptor", {
      url: config.url,
      method: config.method,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      isValidateToken,
      isResetPassword,
      willSkipAuth: shouldSkipAuth,
    });
    if (shouldSkipAuth) {
      console.log("🟢 userManagement axios: Skipping Authorization header for", isValidateToken ? "validateToken" : "resetPassword");
      return config;
    }
    // Add Authorization header with access token for other endpoints
    return addAuthHeader(config);
  },
  (error) => {
    console.error("🔴 userManagement axios: Request interceptor error", error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
userManagementHttps.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log("🟢 userManagement axios: Response received", {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      method: response.config.method,
    });
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    console.log("🔴 userManagement axios: Response error", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: originalRequest?.url,
      method: originalRequest?.method,
      isValidateToken: originalRequest?.url?.includes("/validateToken"),
    });

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Skip token refresh for validateToken and resetPassword endpoints
      const url = originalRequest.url || "";
      if (url.includes("/validateToken") || url.includes("/resetPassword")) {
        console.log("🟡 userManagement axios: Skipping token refresh for", url.includes("/validateToken") ? "validateToken" : "resetPassword", "endpoint");
        return Promise.reject(error);
      }

      try {
        return await handle401Error(error, (config) => userManagementHttps.request(config));
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default userManagementHttps;

